import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { DBProject, DBInvoice } from '@/lib/db-types';
import { computeInvoiceCalculations, type UpsellSummary } from './use-invoice-calculations';

/**
 * VIB-1143 / VIB-1147: Aktive prosjekter med faktureringsoversikt (dashboard-tabell).
 *
 * Tallene gjenbruker prosjektkortets motor (computeInvoiceCalculations) per
 * prosjekt, så de er konsistente med «Gjenstående til fakturering» på kortet
 * (eks. mva, inkl. ufakturert mersalg). Pengekolonnene grosses opp × 1,25 for
 * visning INKL. mva (Salgssum, Fakturert, Gjenstår). Forv. BF (bf_estimated)
 * vises EKS. mva. Kun aktive, ikke-slettede, IKKE-interne prosjekter.
 */

/** Norsk standard MVA (25 %). Systemets beløp lagres eks. mva. */
export const VAT_MULTIPLIER = 1.25;

export interface ActiveProjectInvoiceRow {
  id: string;
  name: string;
  projectCode: string;
  customerName: string;
  advisorId: string | null;
  advisorName: string;
  /** projects.created_at (vi har ingen egen start-dato) */
  createdAt: string | null;
  /** projects.estimated_deadline (forventet sluttfaktura) */
  deadline: string | null;
  /** salgssum inkl. mva (kontraktssum × 1,25) */
  salgssumInkl: number;
  /** fakturert inkl. mva (fakturert eks. mva × 1,25) */
  fakturertInkl: number;
  /** gjenstår inkl. mva (totalt gjenstående × 1,25, inkl. ufakturert mersalg) */
  gjenstaarInkl: number;
  /** forventet bruttofortjeneste, EKS. mva */
  bfEstimated: number;
}

export interface ActiveProjectsInvoiceSummary {
  rows: ActiveProjectInvoiceRow[];
  totalSalgssumInkl: number;
  totalFakturertInkl: number;
  totalGjenstaarInkl: number;
  totalBfEstimated: number;
}

interface ProjectInput {
  id: string;
  name: string;
  project_code: string | null;
  total_amount: number | null;
  bf_estimated: number | null;
  estimated_deadline: string | null;
  created_at: string | null;
  advisor_id: string | null;
  advisor_name: string | null;
  customer_name: string | null;
  is_internal: boolean | null;
}

interface InvoiceInput {
  project_id: string;
  amount: number | null;
  amount_excl_vat: number | null;
  /** VIB-994 (fix, review 21.07): satt KUN for T&M-fakturaer. Må hentes med —
   *  computeInvoiceCalculations bruker den til å ekskludere T&M-fakturaer fra
   *  kontrakt-invoicedExclVat (ellers netter T&M-inntekten bort "gjenstår"). */
  upsell_id?: string | null;
}

interface UpsellInput extends UpsellSummary {
  project_id: string;
}

/**
 * Ren beregning. For hvert aktive, ikke-interne prosjekt kjøres prosjektkortets
 * computeInvoiceCalculations (eks. mva, inkl. mersalg-håndtering); resultatet
 * grosses ×1,25 for inkl.mva-visning. Default-sortering: forventet BF synkende.
 * Holdt separat fra datahentingen så den er enkel å teste og garantert
 * konsistent med prosjektkortet.
 */
export function computeActiveProjectsInvoiceSummary(
  projects: ProjectInput[],
  invoices: InvoiceInput[],
  upsells: UpsellInput[],
): ActiveProjectsInvoiceSummary {
  const invoicesByProject = new Map<string, InvoiceInput[]>();
  for (const inv of invoices) {
    const arr = invoicesByProject.get(inv.project_id) ?? [];
    arr.push(inv);
    invoicesByProject.set(inv.project_id, arr);
  }
  const upsellsByProject = new Map<string, UpsellInput[]>();
  for (const u of upsells) {
    const arr = upsellsByProject.get(u.project_id) ?? [];
    arr.push(u);
    upsellsByProject.set(u.project_id, arr);
  }

  const rows: ActiveProjectInvoiceRow[] = projects
    .filter((p) => p.is_internal !== true)
    .map((p) => {
      const calc = computeInvoiceCalculations(
        p as unknown as DBProject,
        (invoicesByProject.get(p.id) ?? []) as unknown as DBInvoice[],
        upsellsByProject.get(p.id) ?? [],
      );
      return {
        id: p.id,
        name: p.name,
        projectCode: p.project_code ?? '',
        customerName: p.customer_name ?? '',
        advisorId: p.advisor_id ?? null,
        advisorName: p.advisor_name ?? '',
        createdAt: p.created_at ?? null,
        deadline: p.estimated_deadline ?? null,
        salgssumInkl: calc.contractAmount * VAT_MULTIPLIER,
        fakturertInkl: calc.invoicedExclVat * VAT_MULTIPLIER,
        gjenstaarInkl: calc.totalRemaining * VAT_MULTIPLIER,
        bfEstimated: p.bf_estimated ?? 0,
      };
    });

  rows.sort((a, b) => b.bfEstimated - a.bfEstimated);

  return {
    rows,
    totalSalgssumInkl: rows.reduce((s, r) => s + r.salgssumInkl, 0),
    totalFakturertInkl: rows.reduce((s, r) => s + r.fakturertInkl, 0),
    totalGjenstaarInkl: rows.reduce((s, r) => s + r.gjenstaarInkl, 0),
    totalBfEstimated: rows.reduce((s, r) => s + r.bfEstimated, 0),
  };
}

/** PostgREST kan returnere embeddede relasjoner som objekt eller ett-elements array. */
function embedField<T>(rel: unknown, key: string): T | null {
  if (!rel) return null;
  const obj = Array.isArray(rel) ? rel[0] : rel;
  return (obj?.[key] ?? null) as T | null;
}

export function useActiveProjectsInvoiceSummary() {
  return useQuery({
    queryKey: ['active-projects-invoice-summary'],
    queryFn: async (): Promise<ActiveProjectsInvoiceSummary> => {
      const projectsRes = await supabase
        .from('projects')
        .select(
          'id, name, project_code, total_amount, bf_estimated, estimated_deadline, created_at, advisor_id, is_internal, startup_invoice_pct, invoice_terms, advisor:advisor_id(full_name), customer:customer_id(name)',
        )
        .eq('project_status', 'active')
        .is('deleted_at', null);

      if (projectsRes.error) throw projectsRes.error;

      const projectRows = (projectsRes.data ?? []) as Record<string, unknown>[];
      const projects: ProjectInput[] = projectRows.map((row) => ({
        id: row.id as string,
        name: row.name as string,
        project_code: (row.project_code as string | null) ?? null,
        total_amount: (row.total_amount as number | null) ?? null,
        bf_estimated: (row.bf_estimated as number | null) ?? null,
        estimated_deadline: (row.estimated_deadline as string | null) ?? null,
        created_at: (row.created_at as string | null) ?? null,
        advisor_id: (row.advisor_id as string | null) ?? null,
        advisor_name: embedField<string>(row.advisor, 'full_name'),
        customer_name: embedField<string>(row.customer, 'name'),
        is_internal: (row.is_internal as boolean | null) ?? null,
        // startup_invoice_pct + invoice_terms beholdes på objektet for
        // computeInvoiceCalculations (cast til DBProject), selv om de ikke er
        // egne ProjectInput-felter — de påvirker kun milepæl-output vi ignorerer.
        ...(row.startup_invoice_pct != null ? { startup_invoice_pct: row.startup_invoice_pct } : {}),
        ...(row.invoice_terms != null ? { invoice_terms: row.invoice_terms } : {}),
      })) as ProjectInput[];

      // Hent fakturaer + mersalg KUN for de aktive prosjektene. invoices er stor
      // (>1000 rader) så et ufiltrert select treffer Supabase sin rad-grense.
      const ids = projects.map((p) => p.id);
      if (ids.length === 0) {
        return computeActiveProjectsInvoiceSummary([], [], []);
      }

      const [invoicesRes, upsellsRes] = await Promise.all([
        // upsell_id: VIB-994 fix — kreves for å skille T&M-fakturaer fra
        // kontrakt-fakturaer i computeInvoiceCalculations (se InvoiceInput).
        supabase.from('invoices').select('project_id, amount, amount_excl_vat, upsell_id').in('project_id', ids),
        // Samme filter som prosjektkortet (useUpsells): ekskluder soft-deleted.
        supabase
          .from('upsells')
          .select('id, project_id, description, amount_excl_vat, added_to_invoice_id')
          .in('project_id', ids)
          .neq('status', 'deleted'),
      ]);

      if (invoicesRes.error) throw invoicesRes.error;
      if (upsellsRes.error) throw upsellsRes.error;

      return computeActiveProjectsInvoiceSummary(
        projects,
        (invoicesRes.data ?? []) as InvoiceInput[],
        (upsellsRes.data ?? []) as UpsellInput[],
      );
    },
    staleTime: 5 * 60 * 1000,
  });
}
